import { ProCard } from '@ant-design/pro-components'
import { Button, Checkbox, Col, Form, Input, Radio, Row, Select, Switch, Typography, type FormInstance } from 'antd'
import { ScanLine } from 'lucide-react'
import { Authorized } from '@/features/authorization/authorized.tsx'
import { AttachmentUpload, type AttachmentDescriptor } from '@/features/files/index.ts'
import type { LegalEntityFormValues } from './legal-entity-form.model.ts'
import { LegalRepresentativeFields } from './legal-representative-fields.tsx'
import { LegalRepresentativeOcr } from './legal-representative-ocr.tsx'

type LegalEntityFormFieldsProps = {
  canManageAccounts?: boolean
  form: FormInstance<LegalEntityFormValues>
  initialLicense: AttachmentDescriptor[]
  isLongTerm?: boolean
  licenseSourceFile?: File
  recognizePending: boolean
  onFilesChange: (fileIds: number[]) => void
  onLicenseUploaded: (file: File, uploadedFile: { id: number }) => void
  onRunOcr: () => void
}

export function LegalEntityFormFields({
  form,
  canManageAccounts = false,
  initialLicense,
  isLongTerm,
  licenseSourceFile,
  recognizePending,
  onFilesChange,
  onLicenseUploaded,
  onRunOcr
}: LegalEntityFormFieldsProps) {
  return (
    <div className="flex flex-col gap-4 pb-6">
      <section className="scroll-mt-24" id="section-license">
        <ProCard headerBordered title="证照原件" variant="outlined">
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
            <div className="min-w-0">
              <Form.Item
                className="mb-0!"
                extra="支持图片或 PDF；OCR 仅支持 JPEG、PNG、BMP 图片。"
                label="营业执照原件"
                name="businessLicenseFileIds"
                rules={[{ message: '请上传营业执照', min: 1, required: true, type: 'array' }]}
              >
                <AttachmentUpload
                  purpose="organizationLicense"
                  accept="image/jpeg,image/png,image/bmp,.pdf"
                  initialFiles={initialLicense}
                  maxCount={1}
                  multiple={false}
                  onChange={onFilesChange}
                  onUploaded={onLicenseUploaded}
                />
              </Form.Item>
              <div className="mt-3 flex min-w-0 flex-col gap-3">
                <div>
                  <Typography.Text strong>自动识别工商信息</Typography.Text>
                  <Typography.Paragraph className="mt-1! mb-0!" type="secondary">
                    图片上传后自动识别，结果会以候选值展示，确认后才会写入表单。识别失败时会自动删除图片。
                  </Typography.Paragraph>
                </div>
                <Button
                  className="self-start"
                  disabled={!licenseSourceFile || recognizePending}
                  icon={<ScanLine aria-hidden size={16} />}
                  loading={recognizePending}
                  onClick={onRunOcr}
                >
                  重新识别营业执照
                </Button>
              </div>
            </div>
            <div className="min-w-0">
              <LegalRepresentativeOcr form={form} />
            </div>
          </div>
        </ProCard>
      </section>

      <section className="scroll-mt-24" id="section-basic">
        <ProCard headerBordered subTitle="主体身份与经营归属" title="基本信息" variant="outlined">
          <Row gutter={20}>
            <Col lg={24} xs={24}>
              <Form.Item label="经营类型" name="operationMode" rules={[{ required: true }]}>
                <Radio.Group
                  optionType="button"
                  options={[
                    { label: '自营', value: 'selfOperated' },
                    { label: '代理', value: 'agent' }
                  ]}
                />
              </Form.Item>
            </Col>
            <Col lg={12} xs={24}>
              <Form.Item label="主体名称" name="name" rules={[{ max: 255, required: true }]}>
                <Input placeholder="请输入营业执照上的主体名称" />
              </Form.Item>
            </Col>
            <Col lg={12} xs={24}>
              <Form.Item
                label="统一社会信用代码"
                name="unifiedSocialCreditCode"
                normalize={(value: string) => value.toUpperCase()}
                rules={[
                  { message: '请输入统一社会信用代码', required: true },
                  { message: '统一社会信用代码应为 18 位数字或大写字母', pattern: /^[0-9A-Z]{18}$/ }
                ]}
              >
                <Input maxLength={18} placeholder="18 位统一社会信用代码" />
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item label="注册地址" name="registeredAddress" rules={[{ max: 500, required: true }]}>
                <Input placeholder="请输入营业执照上的注册地址" />
              </Form.Item>
            </Col>
          </Row>
        </ProCard>
      </section>

      <LegalRepresentativeFields />

      <section className="scroll-mt-24" id="section-registration">
        <ProCard headerBordered subTitle="补充营业期限与经营范围" title="工商登记" variant="outlined">
          <Row gutter={20}>
            <Col lg={12} xs={24}>
              <Form.Item label="公司类型" name="companyType">
                <Input maxLength={100} placeholder="例如：有限责任公司" />
              </Form.Item>
            </Col>
            <Col lg={12} xs={24}>
              <Form.Item label="注册资本" name="registeredCapital">
                <Input maxLength={100} placeholder="例如：1000 万元人民币" />
              </Form.Item>
            </Col>
            <Col lg={8} md={12} xs={24}>
              <Form.Item label="成立日期" name="establishedOn">
                <Input type="date" />
              </Form.Item>
            </Col>
            <Col lg={8} md={12} xs={24}>
              <Form.Item
                label="营业期限至"
                name="validUntil"
                rules={[{ message: '非长期营业执照必须填写有效期', required: !isLongTerm }]}
              >
                <Input disabled={isLongTerm} type="date" />
              </Form.Item>
            </Col>
            <Col lg={8} md={12} xs={24}>
              <Form.Item label="营业期限" name="isLongTerm" valuePropName="checked">
                <Checkbox
                  onChange={(event) => {
                    if (event.target.checked) {
                      form.setFieldValue('validUntil', undefined)
                    }
                  }}
                >
                  长期
                </Checkbox>
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item label="经营范围" name="businessScope">
                <Input.TextArea maxLength={5000} placeholder="请输入营业执照上的经营范围" rows={5} showCount />
              </Form.Item>
            </Col>
          </Row>
        </ProCard>
      </section>

      {canManageAccounts && (
        <section className="scroll-mt-24" id="section-payment">
          <ProCard
            title="收付款账户"
            subTitle="银行、平台或现金账户；已有账户可停用，历史单据保留快照。"
            variant="outlined"
          >
            <Form.List name="accounts">
              {(fields, { add, remove }) => (
                <div className="space-y-4">
                  {fields.map(({ key, name }) => (
                    <div key={key} className="border-border rounded-lg border p-3">
                      <Form.Item name={[name, 'id']} hidden>
                        <Input />
                      </Form.Item>
                      <div className="grid gap-x-4 sm:grid-cols-2">
                        <Form.Item name={[name, 'name']} label="账户备注" rules={[{ max: 255 }]}>
                          <Input maxLength={255} placeholder="选填，例如：基本户、支付宝收款、备用账户" />
                        </Form.Item>
                        <Form.Item name={[name, 'type']} label="账户类型" rules={[{ required: true }]}>
                          <Select
                            options={[
                              { value: 'bank', label: '银行' },
                              { value: 'platform', label: '收款平台' },
                              { value: 'cash', label: '现金' }
                            ]}
                          />
                        </Form.Item>
                        <Form.Item name={[name, 'accountHolder']} label="开户名称" rules={[{ required: true }]}>
                          <Input />
                        </Form.Item>
                        <Form.Item
                          name={[name, 'bankName']}
                          label="开户银行"
                          dependencies={[['accounts', name, 'type']]}
                          rules={[
                            ({ getFieldValue }) => ({
                              required: getFieldValue(['accounts', name, 'type']) === 'bank',
                              whitespace: true,
                              message: '请填写开户银行'
                            })
                          ]}
                        >
                          <Input maxLength={255} placeholder="请填写开户银行" />
                        </Form.Item>
                        <Form.Item
                          name={[name, 'accountNo']}
                          label="单位账号"
                          dependencies={[['accounts', name, 'type']]}
                          rules={[
                            ({ getFieldValue }) => ({
                              required: getFieldValue(['accounts', name, 'type']) === 'bank',
                              whitespace: true,
                              message: '请填写单位账号'
                            })
                          ]}
                        >
                          <Input maxLength={100} placeholder="请填写单位账号" />
                        </Form.Item>
                        <Form.Item name={[name, 'isEnabled']} label="启用" valuePropName="checked">
                          <Switch />
                        </Form.Item>
                        <Form.Item
                          name={[name, 'qrFileId']}
                          label="收款二维码"
                          getValueProps={(id: number | null) => ({ value: id ? [id] : [] })}
                          getValueFromEvent={(ids: number[]) => ids[0] ?? null}
                        >
                          <AttachmentUpload
                            purpose="organizationAccountQr"
                            accept="image/*"
                            maxCount={1}
                            multiple={false}
                          />
                        </Form.Item>
                      </div>
                      <Authorized permissions={['organization.delete']}>
                        <Button danger type="text" onClick={() => remove(name)}>
                          移除（已有账户保存后停用）
                        </Button>
                      </Authorized>
                    </div>
                  ))}
                  <Button
                    block
                    type="dashed"
                    onClick={() =>
                      add({
                        name: '',
                        type: 'bank',
                        accountHolder: form.getFieldValue('name'),
                        bankName: null,
                        accountNo: null,
                        qrFileId: null,
                        isEnabled: true
                      })
                    }
                  >
                    添加资金账户
                  </Button>
                </div>
              )}
            </Form.List>
          </ProCard>
        </section>
      )}

      <section className="scroll-mt-24" id="section-note">
        <ProCard headerBordered subTitle="仅用于内部管理" title="内部备注" variant="outlined">
          <Form.Item name="note">
            <Input.TextArea maxLength={2000} placeholder="补充内部管理信息" rows={4} showCount />
          </Form.Item>
        </ProCard>
      </section>
    </div>
  )
}
